import { useWallet, WalletContextState } from '@solana/wallet-adapter-react';
import { Connection, PublicKey } from '@solana/web3.js';
import { createContext, useEffect, useState } from 'react';
import { AnchorProvider, setProvider, Program, Idl } from '@coral-xyz/anchor';
import { AnchorWallet, useAnchorWallet } from '@solana/wallet-adapter-react';
import IDL from '../utils/idl/blackjack_contract.json';
import { ReactNode } from 'react';
import { BlackjackContract } from '../utils/types/blackjack_contract';
import { TableData } from '../types.ds';
import { PhantomWalletName } from "@solana/wallet-adapter-phantom";
import { getUserInfo } from '../utils/getUserInfo';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';


interface PlayerContextType {
  playerId: string;
  playerName: string;
  avatar: string;
  createdAt: Date;
  role: number;
  blocked: boolean;
  userWallet: PublicKey | null;
  userShorWallet: string;
  walletBalance: number;
  wallet: WalletContextState;
  tokenBalance: number;
  initialTokenBalance: number;
  withdrawlBalance: number;
  tableList: Array<TableData>;
  playerIdHandler: (id: string) => void;
  playerNameHandler: (name: string) => void;
  playerAvatarHandler: (name: string) => void;
  playerCreatedAtHandler: (name: Date) => void;
  playerRoleHandler: (name: number) => void;
  tokenBalanceHandler: (bal: number) => void;
  initialTokenBalanceHandler: (bal: number) => void;
  withdrawlBalanceHandler: (bal: number) => void;
  tableListhandler: (data: Array<TableData>) => void;
  handleConnectWallet: () => void;
  connection: Connection;
  program?: Program<BlackjackContract> | undefined;
  anchorWallet?: AnchorWallet | undefined;
}

export const PlayerContext = createContext<PlayerContextType | undefined>(
  undefined
);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const PlayerProvider = ({ children }: { children: ReactNode }) => {
  const { setVisible: setModalVisible } = useWalletModal();
  const [playerId, setPlayerId] = useState<string>('');
  const [playerName, setPlayerName] = useState<string>('');
  const [avatar, setAvatar] = useState<string>('');
  const [createdAt, setCreatedAt] = useState<Date>(new Date());
  const [role, setRole] = useState<number>(3);
  const [blocked, setBlocked] = useState<boolean>(false);
  const [userWallet, setUserWallet] = useState<PublicKey | null>(null);
  const [userShorWallet, setUserShortWallet] = useState<string>("---");
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const [tableList, setTableList] = useState<Array<TableData>>([]);
  // BALANCE WHICH THE USER CAN WITHDRAW //
  const [withdrawlBalance, setWithdrawlBalance] = useState<number>(0);
  const [program, setProgram] = useState<Program<BlackjackContract> | undefined>();
  // KEEP TRACK OF BALANCE BEFORE THE RESULTS ("GAMES END") EG. WHEN USER WITHDRAWS, RECHARGE ETC //
  const [initialTokenBalance, setInitialTokenBalance] = useState<number>(0);
  // const connection = new Connection('http://127.0.0.1:8899', 'confirmed');

  const NETWORK =
    process.env.REACT_APP_SOLANA_NETWORK || 'https://api.devnet.solana.com';
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const wallet = useWallet();
  const anchorWallet = useAnchorWallet();

  const handleConnectWallet = async () => {
    try {
      if (wallet.wallet) {
        wallet.select(PhantomWalletName);
        await wallet.connect();
      } else {
        setModalVisible(true)
      }
    } catch (e) {

    }
  }

  function playerIdHandler(id: string) {
    setPlayerId(id);
  }

  function playerNameHandler(name: string) {
    setPlayerName(name);
  }

  function playerAvatarHandler(avatar: string) {
    setAvatar(avatar);
  }

  function playerCreatedAtHandler(date: Date) {
    setCreatedAt(date);
  }

  function playerRoleHandler(role: number) {
    setRole(role)
  }

  function playerBlockHandler(blocked: boolean) {
    setBlocked(blocked)
  }

  function tokenBalanceHandler(bal: number) {
    setTokenBalance(bal);
  }

  function withdrawlBalanceHandler(bal: number) {
    setWithdrawlBalance(bal);
  }

  function initialTokenBalanceHandler(bal: number) {
    setInitialTokenBalance(bal);
  }

  function tableListhandler(data: Array<TableData>) {
    setTableList([...data]);
  }

  const connection = new Connection(NETWORK, 'confirmed');
  useEffect(() => {
    const getUserInfo = async () => {
      if (!anchorWallet) {
        return;
      }
      setUserWallet(wallet.publicKey);
      if (!userWallet) {
        return;
      }

      if (!connection) {
        return;
      }
      setUserShortWallet(`${userWallet.toString().slice(0, 4)}...${userWallet.toString().slice(-4)}`);
      const provider = new AnchorProvider(connection, anchorWallet, {
        preflightCommitment: 'processed',
      });
      setProvider(provider);
      const program = new Program<BlackjackContract>(IDL as BlackjackContract, provider);
      setProgram(program);
      const balance = await connection.getBalance(userWallet);

      if (balance) {
        setWalletBalance(balance);
      }
    };
    getUserInfo();
  }, [wallet, userWallet, anchorWallet]);
  // function userWalletHandler(pubKey: PublicKey) {
  // }

  useEffect(() => {
    if (anchorWallet?.publicKey) {
      const walletConnection = wallet.disconnecting;
      if (walletConnection) {
        tokenBalanceHandler(0);
        playerIdHandler('');
        // reset();
        return;
      }

      // setUserWallet(wallet.publicKey);
      if (anchorWallet) {
        try {
          const userInfo = async (walletKey: string | undefined) => {
            const user = await getUserInfo(walletKey);
            if (user === undefined) {
              return;
            }

            if (user && user.userData && user.userData._id) {
              const balance = +user.userData.balance.toFixed(2);
              const freeTokenBalance = +user.userData.freeTokens.toFixed(2);
              const totalBalance =
                freeTokenBalance > 0 ? balance + freeTokenBalance : balance;
              withdrawlBalanceHandler(balance);
              tokenBalanceHandler(
                // +user.userData.balance.toFixed(2)
                totalBalance
              );
              initialTokenBalanceHandler(
                // +user.userData.balance.toFixed(2)
                totalBalance
              );
              playerIdHandler(user.userData._id);
              playerNameHandler(user.userData.name);
              playerAvatarHandler(user.userData.avatar);
              //   setAvatarUrl(user.userData.avatar)
              const date = new Date(user.userData.created_at);
              playerCreatedAtHandler(date);
              playerRoleHandler(user.userData.role);
              playerBlockHandler(user.userData.blocked);
            }

            if (user && !user.userData) {
              tokenBalanceHandler(0);
              playerIdHandler('');
            }
          };

          if (anchorWallet) {
            userInfo(anchorWallet?.publicKey.toString());
          }
        } catch (error: any) {
        }
      } else {
      }
    } else {
      tokenBalanceHandler(0);
      playerIdHandler('');
      //   reset();
      return;
    }
  }, [
    anchorWallet,
    wallet,
    userWallet,
  ]);

  const value = {
    playerId,
    playerName,
    avatar,
    createdAt,
    role,
    blocked,
    userWallet,
    userShorWallet,
    walletBalance,
    tokenBalance,
    initialTokenBalance,
    withdrawlBalance,
    connection,
    tableList,
    playerIdHandler,
    playerNameHandler,
    playerAvatarHandler,
    playerCreatedAtHandler,
    playerRoleHandler,
    playerBlockHandler,
    tokenBalanceHandler,
    initialTokenBalanceHandler,
    withdrawlBalanceHandler,
    tableListhandler,
    handleConnectWallet,
    wallet,
    program,
    anchorWallet,
  };
  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  );
};

export default PlayerProvider;
